var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment, h } from 'preact';
import { useCallback, useEffect, useId, useRef, useState } from 'preact/hooks';
import './BackInStockPopup.scss';
import { KlaviyoConfigError, KlaviyoRequestError, subscribeToBackInStock, } from './klaviyoBackInStock';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function CloseIcon() {
    return (h("svg", { "aria-hidden": true, focusable: "false", role: "presentation", className: "icon icon-close", viewBox: "0 0 192 192" },
        h("path", { d: "M150 42 42 150M150 150 42 42", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "8", fill: "none" })));
}
export function BackInStockPopup({ open, onClose, shopifyVariantId, }) {
    const headingId = useId();
    const fieldId = useId();
    const errorBoxRef = useRef(null);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [feedback, setFeedback] = useState({ type: 'hidden' });
    useEffect(() => {
        if (!open) {
            setEmail('');
            setFeedback({ type: 'hidden' });
            setLoading(false);
            setIsSubscribed(false);
        }
    }, [open]);
    const showError = useCallback((message) => {
        setFeedback({ type: 'error', message });
        queueMicrotask(() => {
            var _a;
            (_a = errorBoxRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
    }, []);
    const handleSubmit = (e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        if (isSubscribed || loading)
            return;
        setFeedback({ type: 'hidden' });
        if (!shopifyVariantId) {
            showError('This item is unavailable right now. Please try again later.');
            return;
        }
        const trimmedEmail = email.trim();
        if (!trimmedEmail || !EMAIL_PATTERN.test(trimmedEmail)) {
            showError('Please enter a valid email address.');
            return;
        }
        setLoading(true);
        try {
            yield subscribeToBackInStock({ channel: 'EMAIL', email: trimmedEmail, shopifyVariantId });
            setIsSubscribed(true);
        }
        catch (err) {
            const message = err instanceof KlaviyoConfigError
                ? 'Sign-up is unavailable right now.'
                : err instanceof KlaviyoRequestError
                    ? err.message
                    : 'Something went wrong. Please try again later.';
            console.error('[BackInStockPopup]', err);
            showError(message);
        }
        finally {
            setLoading(false);
        }
    });
    if (!open)
        return null;
    return (h("div", { className: "bis-modal-overlay", role: "presentation", onMouseDown: (e) => {
            if (e.target === e.currentTarget)
                onClose();
        } },
        h("div", { className: "bis-popup", role: "dialog", "aria-modal": true, "aria-labelledby": headingId, onMouseDown: (e) => e.stopPropagation() },
            h("button", { type: "button", title: "Close", "aria-label": "Close", className: "bis-popup__close", onClick: onClose },
                h(CloseIcon, null)),
            isSubscribed ? (h("div", { className: "bis-popup__success" },
                h("h2", { className: "bis-popup__success-heading" }, "You're all set"),
                h("p", { className: "bis-popup__success-description" }, "Thank you for your request! We'll let you know when your item lands in the nest."))) : (h(Fragment, null,
                h("h2", { className: "bis-popup__heading", id: headingId }, "Request this item"),
                h("p", { className: "bis-popup__description" }, "This style is currently not available. If it's coming soon, we'll let you know when it lands in the nest. If it's sold out, there is still a chance it could come back in stock."),
                h("form", { className: "bis-popup__form", onSubmit: handleSubmit },
                    h("input", { id: fieldId, type: "email", name: "email", className: "bis-popup__input", placeholder: "Email address", value: email, onChange: (e) => setEmail(e.currentTarget.value), autoComplete: "email", required: true }),
                    h("button", { type: "submit", className: `bis-popup__submit ${loading ? 'is-loading' : ''}`, disabled: loading }, loading ? 'Submitting…' : 'Submit request'),
                    h("div", { ref: errorBoxRef, role: "alert", className: `bis-popup__errors ${feedback.type !== 'hidden' ? 'is-visible' : ''}` }, feedback.type === 'error' ? (h("div", { className: "bis-popup__error" }, feedback.message)) : null)))))));
}
